import React from "react";
import { SmallCaps } from "@/components/bits";
import { PRODUCT } from "@/config/product";

// Very dark swatch fills (Black) get a white selection ring — the default
// ink ring disappears against the dark fill.
const isDarkFill = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b < 80;
};

// Colour swatches on their own stable row — the label lives on its own
// line, so picking a colour never shifts the swatches. An outer ink ring
// marks the selection (no scale transforms, so they stay sharp).
export default function ColourSelector({
  values,
  value,
  onChange,
  testIdPrefix = "colour",
  hideLabel = false,
  statusFor = null,
  thumbs = false,
}) {
  return (
    <div>
      {!hideLabel && (
        <SmallCaps data-testid={`${testIdPrefix}-label`}>
          {value ? `COLOUR: ${value.name.toUpperCase()}` : "COLOUR"}
        </SmallCaps>
      )}
      <div
        data-testid={`${testIdPrefix}-options`}
        data-lenis-prevent
        className={`${
          thumbs ? "mt-2.5 gap-2.5" : "mt-2.5 gap-3"
        } flex overflow-x-auto no-scrollbar pb-1.5 sm:flex-wrap`}
      >
        {values.map((c, i) => {
          const active = value?.id === c.id;
          const status = statusFor ? statusFor(c) : null;
          // Thumbnail mode (PDP): rounded-square mini product shot per
          // colour, hot-pink 2px outline when selected, grey border when not.
          if (thumbs) {
            const img = PRODUCT.colourImages?.[c.id];
            // Segment the row: a vertical grey rule between the orderable
            // colourways (Preorder) and the coming-soon ones.
            const prev = i > 0 && statusFor ? statusFor(values[i - 1]) : null;
            const showDivider = i > 0 && status !== prev;
            return (
              <React.Fragment key={c.id}>
                {showDivider && (
                  <span
                    aria-hidden="true"
                    data-testid={`${testIdPrefix}-divider`}
                    className="w-px h-14 sm:h-16 shrink-0 bg-ink/15 self-start"
                  />
                )}
              <button
                data-testid={`${testIdPrefix}-option-${c.id}`}
                aria-label={c.name}
                aria-pressed={active}
                onClick={() => onChange(c)}
                className="shrink-0 flex flex-col items-center active:scale-95 transition-transform duration-150"
              >
                <span
                  className={`block w-14 h-14 sm:w-16 sm:h-16 rounded-[5px] overflow-hidden bg-[#F4F3F0] border-2 transition-[border-color] duration-150 ${
                    active ? "border-[#FF5BA7]" : "border-ink/10"
                  }`}
                >
                  {img ? (
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      draggable="false"
                    />
                  ) : (
                    <span
                      className="block w-full h-full"
                      style={{ backgroundColor: c.hex }}
                    />
                  )}
                </span>
              </button>
              </React.Fragment>
            );
          }
          const ring =
            "rounded-full border-[3px] transition-[border-color] duration-150 " +
            (active
              ? isDarkFill(c.hex)
                ? "border-white shadow-[2px_2px_0px_#111111]"
                : "border-ink shadow-[2px_2px_0px_#111111]"
              : "border-ink/15");
          return (
            <button
              key={c.id}
              data-testid={`${testIdPrefix}-option-${c.id}`}
              aria-label={c.name}
              aria-pressed={active}
              onClick={() => onChange(c)}
              className={
                status
                  ? "shrink-0 flex flex-col items-center active:scale-95 transition-transform duration-150"
                  : `shrink-0 p-1 active:scale-95 ${ring}`
              }
            >
              <span
                className={`block w-12 h-12 rounded-full ${status ? ring : ""}`}
                style={{
                  backgroundColor: c.hex,
                  boxShadow: active
                    ? undefined
                    : "inset 0 0 0 1px rgba(17,17,17,0.12)",
                }}
              />
              {status && (
                <span className="mt-1.5 block w-16 text-center">
                  <span className="block text-[10px] font-bold leading-tight text-ink">
                    {c.name}
                  </span>
                  <span
                    className={`block text-[9px] font-semibold leading-tight ${
                      status === "Preorder" ? "text-[#FF6BAA]" : "text-ink/45"
                    }`}
                  >
                    {status}
                  </span>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
